import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import ImageUploader from '../components/ImageUploader';
import { useAuth } from '../context/AuthContext';

const Centres = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [centres, setCentres] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [centresRes, vendorsRes] = await Promise.all([
          api.get('/admin/centres'),
          api.get('/admin/vendors')
        ]);
        setCentres(centresRes.data.data || []);
        setVendors(vendorsRes.data.data || []);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    if (location.state?.openAddModal) {
      setIsAddModalOpen(true);
    }
  }, [location.state]);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    description: '',
    coverImage: '',
    isLive: false,
    isVrReady: false,
    adVideoUrl: '',
    isAdSkippable: false,
    email: '',
    password: '',
    vendorId: '',
    vendors: [],
    streamUrls: {
      mainCam: '',
      sideCam: '',
      altarCam: ''
    },
    cameras: []
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('streamUrls.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        streamUrls: {
          ...formData.streamUrls,
          [field]: value
        }
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAddCamera = () => {
    setFormData({
      ...formData,
      cameras: [...(formData.cameras || []), { angleName: '', rtmpUrl: '' }]
    });
  };

  const handleRemoveCamera = (index) => {
    const newCameras = (formData.cameras || []).filter((_, i) => i !== index);
    setFormData({ ...formData, cameras: newCameras });
  };

  const handleCameraChange = (index, field, value) => {
    const newCameras = [...(formData.cameras || [])];
    newCameras[index][field] = value;

    // Auto generate RTMP URL if angle name is set and rtmpUrl is empty
    if (field === 'angleName' && !newCameras[index].rtmpUrl && value.trim() !== '') {
      const centreSlug = (formData.name || 'centre').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 10);
      const angleSlug = value.toLowerCase().replace(/[^a-z0-9]/g, '');
      const randomId = Math.random().toString(16).substring(2, 10);
      newCameras[index].rtmpUrl = `rtmp://localhost:1935/live/sk_${centreSlug}_${angleSlug}_${randomId}`;
    }

    setFormData({ ...formData, cameras: newCameras });
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '',
      location: '',
      description: '',
      coverImage: '',
      isLive: false,
      isVrReady: false,
      adVideoUrl: '',
      isAdSkippable: false,
      email: '',
      password: '',
      vendorId: '',
      vendors: [],
      streamUrls: {
        mainCam: '',
        sideCam: '',
        altarCam: ''
      },
      cameras: []
    });
  };

  const handleEditCentre = (centre) => {
    setEditingId(centre._id);
    setFormData({
      name: centre.name,
      location: centre.location,
      description: centre.description || '',
      coverImage: centre.coverImage || '',
      isLive: centre.isLive || false,
      isVrReady: centre.isVrReady || false,
      adVideoUrl: centre.adVideoUrl || '',
      isAdSkippable: centre.isAdSkippable || false,
      vendorId: centre.vendorId?._id || centre.vendorId || '',
      vendors: (centre.vendors || []).map(v => v._id || v),
      streamUrls: centre.streamUrls || { mainCam: '', sideCam: '', altarCam: '' },
      cameras: centre.cameras || [],
      password: '',  // always empty on edit — leave blank to keep current
      email: centre.trusteeId?.email || ''
    });
    setIsAddModalOpen(true);
  };
  


  const handleSaveCentre = async () => {
    if (!formData.name || !formData.location) {
      alert("Please enter Centre Name and Location.");
      return;
    }

    try {
      if (editingId) {
        const response = await api.put('/admin/centres/' + editingId, formData);
        const updatedCentre = response.data.data || response.data;
        setCentres(centres.map(t => t._id === editingId ? updatedCentre : t));
        alert(`✅ ${formData.name} updated successfully!`);
      } else {
        const response = await api.post('/admin/centres', formData);
        const { centre: newCentre, loginEmail } = response.data.data || {};
        setCentres(prev => [...prev, newCentre || response.data.data]);
        if (loginEmail) {
          alert(`✅ Centre portal provisioned!\n\n📧 Login Email: ${loginEmail}\n🔑 Password: (as you set)\n\nShare these credentials with the Centre Trust.`);
        } else {
          alert(`✅ ${formData.name} added successfully!`);
        }
      }
      
      setIsAddModalOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving centre:", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      alert(`Failed to save centre.\nReason: ${errorMessage}`);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return alert("Error: Centre ID is missing.");
    if (window.confirm('Are you sure you want to delete this centre?')) {
      try {
        await api.delete('/admin/centres/' + id);
        setCentres(prev => prev.filter(t => t._id !== id));
        alert("🗑️ Centre deleted successfully.");
      } catch (error) {
        console.error("❌ Error deleting centre:", error.response?.data || error.message);
        alert("Failed to delete centre: " + (error.response?.data?.message || error.message));
      }
    }
  };

  const editingCentre = editingId ? centres.find(t => t._id === editingId) : null;

  return (
    <div className="p-8 relative">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="font-serif text-3xl font-bold text-[#2A2858]">Registered Centres</h2>
          <p className="mt-1 text-stone-500 text-sm">Manage your sacred destinations</p>
        </div>
        {/* Only ADMIN can add new centres */}
        {isAdmin && (
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="bg-gradient-to-r from-[#E8B84B] to-yellow-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg hover:scale-105 transition-transform flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-sm font-bold">add</span> Add Centre
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-20 text-stone-500 font-bold animate-pulse">
          Loading Centres from Database...
        </div>
      ) : centres.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-stone-200">
          <p className="text-stone-500 font-bold">No centres found in the database. Please add one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
          {centres.map((centre) => (
            <div key={centre._id} className="bg-white rounded-2xl border border-[#E8B84B]/20 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden hover:shadow-lg transition-all group">
              <div className="h-40 bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center relative overflow-hidden">
                  {centre.coverImage ? (
                    <img src={centre.coverImage} alt={centre.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  ) : (
                    <span className="material-symbols-outlined text-8xl text-stone-300 opacity-50 group-hover:scale-110 transition-transform duration-500">account_balance</span>
                  )}
                  {centre.isLive && (
                    <span className="absolute top-4 right-4 bg-red-50 text-red-600 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 border border-red-200 shadow-sm">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span> LIVE
                    </span>
                  )}
                  {centre.isVrReady && (
                    <span className="absolute top-4 left-4 bg-purple-50 text-purple-600 text-[9px] font-black tracking-widest px-2.5 py-1 rounded-full flex items-center gap-1 border border-purple-200 shadow-sm uppercase">
                      <span className="material-symbols-outlined text-[12px]">vrpanoram_fill</span> VR Ready
                    </span>
                  )}
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-bold text-stone-800 mb-1">{centre.name}</h3>
                <p className="text-sm text-stone-500 flex items-center gap-1.5 mb-2">
                  <span className="material-symbols-outlined text-[16px] text-[#E8B84B]">location_on</span> {centre.location}
                </p>
                {centre.trusteeId?.email ? (
                  <p className="text-xs text-stone-600 flex items-center gap-1.5 mb-4 bg-[#E8B84B]/10 px-2.5 py-1.5 rounded-lg border border-[#E8B84B]/10 w-fit">
                    <span className="material-symbols-outlined text-[14px] text-[#2A2858]">mail</span>
                    <span className="font-medium truncate">{centre.trusteeId.email}</span>
                  </p>
                ) : (
                  <p className="text-[10px] text-stone-400 italic flex items-center gap-1.5 mb-4">
                    <span className="material-symbols-outlined text-[14px] text-stone-300">mail_lock</span>
                    <span>No portal login setup</span>
                  </p>
                )}
                <p className="text-sm text-stone-600 line-clamp-2 mb-4 h-10">{centre.description}</p>
                
                {/* Stream Key Display */}
                <div className="mb-4 p-3 bg-stone-50 rounded-xl border border-stone-100 flex items-center justify-between group/key">
                    <div className="overflow-hidden">
                        <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest leading-none mb-1">Stream Key</p>
                        <p className="text-xs font-mono text-stone-700 truncate font-bold">{centre.streamKey || 'No key generated'}</p>
                    </div>
                    <button 
                        onClick={() => {
                            navigator.clipboard.writeText(centre.streamKey);
                            alert('🙏 Stream Key copied to clipboard!');
                        }}
                        className="opacity-0 group-hover/key:opacity-100 p-1.5 hover:bg-[#E8B84B]/10 rounded-lg text-[#E8B84B] transition-all"
                        title="Copy Key"
                    >
                        <span className="material-symbols-outlined text-[18px]">content_copy</span>
                    </button>
                </div>

                <div className="pt-4 border-t border-stone-100 flex justify-between items-center">
                    {/* CENTRE users can only edit their own centre; ADMIN can edit any */}
                    {(isAdmin || (user?.role === 'CENTRE' && centre.trusteeId === user?.id)) && (
                      <button onClick={() => handleEditCentre(centre)} className="text-[#2A2858] font-bold text-sm hover:text-[#E8B84B] transition-colors flex items-center gap-1">
                        <span className="material-symbols-outlined text-[18px]">edit_square</span> Edit
                      </button>
                    )}
                    {/* Only ADMIN can delete centres */}
                    {isAdmin && (
                      <button onClick={() => handleDelete(centre._id)} className="text-stone-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50">
                        <span className="material-symbols-outlined text-[20px]">delete</span>
                      </button>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Centre Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-fade-in max-h-[90vh] flex flex-col">
            <div className="bg-[#FAF9F6] border-b border-[#E8B84B]/20 px-6 py-4 flex justify-between items-center shrink-0">
              <h3 className="font-serif text-xl font-bold text-[#2A2858]">
                {editingId ? 'Edit Centre' : 'Add New Centre'}
              </h3>
              <button onClick={() => { setIsAddModalOpen(false); resetForm(); }} className="text-stone-400 hover:text-red-500 transition-colors">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <form className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Centre Name</label>
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Shri Ram Janmabhoomi" 
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B]" 
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Location</label>
                <input 
                  type="text" 
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g. Ayodhya, UP" 
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B]" 
                />
              </div>
              
              {/* Portal Credentials */}
              {!editingId ? (
                // ── ADD MODE: full email + password setup ──
                <div className="bg-[#E8B84B]/10 border border-[#E8B84B]/30 rounded-xl p-4 space-y-3">
                  <p className="text-[10px] font-black text-[#2A2858] uppercase tracking-[0.15em] flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[14px]">lock</span>
                    Portal Login Credentials
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-stone-500 uppercase mb-1">Login Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="name@centre.com"
                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-stone-500 uppercase mb-1">Portal Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Min 6 characters"
                          className="w-full border border-stone-200 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B]"
                        />
                        <button 
                          type="button" 
                          onClick={() => setShowPassword(v => !v)} 
                          style={{ 
                            position: 'absolute', 
                            right: '0.75rem', 
                            top: '50%', 
                            transform: 'translateY(-50%)', 
                            background: 'none', 
                            border: 'none', 
                            cursor: 'pointer', 
                            color: '#666',
                            fontSize: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                          }}
                        >
                          {showPassword ? '🙈' : '👁️'}
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="text-[9px] text-stone-400 italic">Leave blank to add a centre without a portal account. Fill both to provision access instantly.</p>
                </div>
              ) : (
                // ── EDIT MODE: email & password setup ──
                <div className="bg-[#E8B84B]/10 border border-[#E8B84B]/30 rounded-xl p-4 space-y-3">
                  <p className="text-[10px] font-black text-[#2A2858] uppercase tracking-[0.15em] flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[14px]">lock</span>
                    Portal Login Credentials
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-stone-500 uppercase mb-1">Login Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="name@centre.com"
                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-stone-500 uppercase mb-1">Portal Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Leave blank to keep current"
                          className="w-full border border-stone-200 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B]"
                        />
                        <button 
                          type="button" 
                          onClick={() => setShowPassword(v => !v)} 
                          style={{ 
                            position: 'absolute', 
                            right: '0.75rem', 
                            top: '50%', 
                            transform: 'translateY(-50%)', 
                            background: 'none', 
                            border: 'none', 
                            cursor: 'pointer', 
                            color: '#666',
                            fontSize: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                          }}
                        >
                          {showPassword ? '🙈' : '👁️'}
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="text-[9px] text-stone-400 italic">
                    {editingCentre?.trusteeId
                      ? "Edit the login email or enter a new password (min 6 chars) to update credentials."
                      : "Enter an email and password to provision a portal account for this centre."}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Description</label>
                <textarea 
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3" 
                  placeholder="Short description of the centre..." 
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B]"
                ></textarea>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Centre Cover Photo (Cloudinary)</label>
                <div className="flex flex-col gap-4 bg-stone-50 p-4 rounded-xl border border-stone-100">
                  {formData.coverImage ? (
                    <div className="w-full aspect-video rounded-xl overflow-hidden border-2 border-[#E8B84B]/30 shadow-md relative group">
                      <img src={formData.coverImage} alt="Preview" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-[10px] font-black uppercase tracking-widest">Current Image</span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full aspect-video rounded-xl border-2 border-dashed border-stone-200 flex flex-col items-center justify-center bg-white">
                      <span className="material-symbols-outlined text-stone-200 text-4xl mb-2">image</span>
                      <span className="text-[10px] text-stone-400 font-black uppercase tracking-widest">No Image Selected</span>
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2">
                    <ImageUploader 
                      buttonText={formData.coverImage ? "Change Sacred Photo" : "Upload Centre Photo"}
                      onUploadSuccess={(url) => setFormData({...formData, coverImage: url})}
                      folder="centres"
                    />
                    <p className="text-[10px] text-stone-400 italic flex items-center gap-1">
                      <span className="material-symbols-outlined text-[12px]">verified_user</span>
                      Cloudinary secure storage enabled
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2">
                <div className="flex items-center gap-3">
                  <input 
                    type="checkbox" 
                    id="isLive" 
                    name="isLive"
                    checked={formData.isLive}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-[#2A2858] border-stone-300 rounded focus:ring-[#2A2858]" 
                  />
                  <label htmlFor="isLive" className="text-sm font-bold text-stone-700 cursor-pointer">Live Streaming</label>
                </div>

                <div className="flex items-center gap-3">
                  <input 
                    type="checkbox" 
                    id="isVrReady" 
                    name="isVrReady"
                    checked={formData.isVrReady}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-[#2A2858] border-stone-300 rounded focus:ring-[#2A2858]" 
                  />
                  <label htmlFor="isVrReady" className="text-sm font-bold text-stone-700 cursor-pointer text-purple-600">VR Ready Experience</label>
                </div>
              </div>

              <div className="border-t border-stone-100 pt-4 mt-2 space-y-3">
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Live Stream Configuration</label>
                
                {/* Dynamic Camera Angles */}
                <div className="space-y-3 border border-stone-200 rounded-xl p-4 bg-stone-50">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider">Multi-Angle Cameras</label>
                    <button 
                      type="button" 
                      onClick={handleAddCamera}
                      className="bg-white border border-[#E8B84B]/30 text-[#E8B84B] px-3 py-1 rounded-lg text-xs font-bold hover:bg-[#E8B84B] hover:text-white transition-colors flex items-center gap-1"
                    >
                      <span className="material-symbols-outlined text-[14px]">add</span> Add Camera
                    </button>
                  </div>
                  
                  {(!formData.cameras || formData.cameras.length === 0) ? (
                    <div className="text-center py-4 bg-white border border-stone-100 rounded-lg">
                      <p className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">No extra cameras added</p>
                    </div>
                  ) : (
                    formData.cameras.map((camera, index) => (
                      <div key={index} className="flex flex-col gap-2 bg-white p-3 border border-stone-200 rounded-lg relative group">
                        <button 
                          type="button" 
                          onClick={() => handleRemoveCamera(index)}
                          className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <span className="material-symbols-outlined text-[16px]">close</span>
                        </button>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-6">
                          <div>
                            <label className="block text-[9px] font-bold text-stone-500 uppercase mb-1">Angle Name</label>
                            <input
                              type="text"
                              value={camera.angleName}
                              onChange={(e) => handleCameraChange(index, 'angleName', e.target.value)}
                              list={`camera-suggestions-${index}`}
                              placeholder="e.g. Front View"
                              className="w-full border border-stone-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-[#E8B84B]"
                            />
                            <datalist id={`camera-suggestions-${index}`}>
                              <option value="Front View" />
                              <option value="Side View" />
                              <option value="Altar Cam" />
                              <option value="Top View" />
                              <option value="Wide Angle" />
                              <option value="Priest Cam" />
                              <option value="Main Gate" />
                              <option value="Drone View" />
                            </datalist>
                          </div>
                          <div>
                            <label className="block text-[9px] font-bold text-stone-500 uppercase mb-1">RTMP / Stream URL</label>
                            <div className="relative">
                              <input
                                type="text"
                                value={camera.rtmpUrl}
                                onChange={(e) => handleCameraChange(index, 'rtmpUrl', e.target.value)}
                                placeholder="rtmp://... or https://..."
                                className="w-full border border-stone-200 rounded-lg pl-3 pr-10 py-1.5 text-[11px] font-mono focus:outline-none focus:border-[#E8B84B]"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (camera.rtmpUrl) {
                                    navigator.clipboard.writeText(camera.rtmpUrl);
                                    alert('✅ RTMP URL copied!');
                                  }
                                }}
                                className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-stone-400 hover:text-[#E8B84B] transition-colors"
                                title="Copy URL"
                              >
                                <span className="material-symbols-outlined text-[16px]">content_copy</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-stone-500 uppercase mb-1 text-[#2A2858]">Advertisement Video URL</label>
                  <input
                    type="text"
                    name="adVideoUrl"
                    value={formData.adVideoUrl}
                    onChange={handleInputChange}
                    placeholder="e.g. https://example.com/ad.mp4 (Plays randomly for free users)"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] bg-rose-50/50"
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <input 
                      type="checkbox" 
                      id="isAdSkippable" 
                      name="isAdSkippable"
                      checked={formData.isAdSkippable}
                      onChange={handleInputChange}
                      className="w-3.5 h-3.5 text-[#2A2858] border-stone-300 rounded focus:ring-[#2A2858]" 
                    />
                    <label htmlFor="isAdSkippable" className="text-[10px] font-bold text-stone-600 cursor-pointer">Allow users to skip this ad</label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Assign Vendors to Centre</label>
                <p className="text-[10px] text-stone-400 mb-3 italic">Select one or more approved vendors who can manage offerings for this centre.</p>
                <div className="max-h-40 overflow-y-auto border border-stone-200 rounded-xl p-3 bg-stone-50 space-y-2">
                  {vendors.filter(v => v.approved).length === 0 ? (
                    <p className="text-xs text-stone-400 italic text-center py-2">No approved vendors available.</p>
                  ) : (
                    vendors.filter(v => v.approved).map(vendor => (
                      <label key={vendor._id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white cursor-pointer transition-colors border border-transparent hover:border-stone-200">
                        <input
                          type="checkbox"
                          checked={formData.vendors.includes(vendor._id)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...formData.vendors, vendor._id]
                              : formData.vendors.filter(id => id !== vendor._id);
                            setFormData({
                              ...formData,
                              vendors: updated,
                              vendorId: updated[0] || ''
                            });
                          }}
                          className="w-4 h-4 text-[#2A2858] border-stone-300 rounded focus:ring-[#2A2858]"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-bold text-stone-700">{vendor.userId?.name || 'Unnamed Vendor'}</span>
                          <span className="text-[10px] text-stone-400 ml-2">({vendor.shopName || vendor.type})</span>
                        </div>
                        <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">Approved</span>
                      </label>
                    ))
                  )}
                </div>
                {formData.vendors.length > 0 && (
                  <p className="text-[10px] text-[#2A2858] font-bold mt-2">{formData.vendors.length} vendor(s) assigned</p>
                )}
              </div>

              <div className="pt-6 flex gap-4">
                <button type="button" onClick={() => { setIsAddModalOpen(false); resetForm(); }} className="w-1/2 bg-stone-100 text-stone-600 font-bold py-2.5 rounded-lg hover:bg-stone-200 transition-colors">
                  Cancel
                </button>
                <button type="button" onClick={handleSaveCentre} className="w-1/2 bg-[#2A2858] text-white font-bold py-2.5 rounded-lg hover:bg-[#14122B] transition-colors shadow-lg">
                  {editingId ? 'Update Centre' : 'Save Centre'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Centres;